

import React, { useMemo } from 'react'
import ReactApexChart from 'react-apexcharts'
import '../../home-style.scss'
import dayjs from 'dayjs'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import useEnergyGraphContainerStyles from './styles'
import { LinearBuffer } from '../../../PlantTechicalView/components/utils/LinearBuffer'

const EnergyGraphContainer = ({
  openTable,
  mapTotalenergySeries = [],
  chartData = {},
  chartRef,
  isData,
  totalEnergygraphDuration,
}) => {
  const classes = useEnergyGraphContainerStyles()

  //Determine if we should use daily format (<= 1 month) or monthly (> 1 month)
  const isDailyFormat = useMemo(() => {
    if (!mapTotalenergySeries?.[0]?.data || mapTotalenergySeries[0].data.length === 0) {
      return false
    }

    if (["1m", "mtd"].includes(totalEnergygraphDuration)) return true

    if (totalEnergygraphDuration === 'custom') {
      const first = mapTotalenergySeries[0].data[0]?.x
      if (typeof first === 'string') {
        const dateStr = first.includes('T') ? first.split('T')[0] : first
        // YYYY-MM-DD has 3 parts; treat as daily
        return dateStr.split('-').length === 3
      }
    }
    return false
  }, [mapTotalenergySeries, totalEnergygraphDuration])

  const processedData = useMemo(() => {
    const rawData = mapTotalenergySeries?.[0]?.data || []

    let processedDataPoints = rawData
      .map((item) => {
        const rawX = typeof item.x === 'string' ? item.x : String(item.x)
        const dateStr = rawX.includes('T') ? rawX.split('T')[0] : rawX
        const label = isDailyFormat
          ? dayjs(dateStr).isValid() ? dayjs(dateStr).format('DD-MM') : dateStr
          : dayjs(dateStr).isValid() ? dayjs(dateStr).format('MMM-YYYY') : dateStr
        const val = Number(item.y)
        return {
          x: label, 
          y: val,
          originalValue: val,
          originalDate: dateStr,
        }
      })
      .filter((item) => !isNaN(item.y))
      .sort((a, b) => new Date(a.originalDate) - new Date(b.originalDate))
    // limit to the most recent 22 to avoid overcrowding
    if (!isDailyFormat) {
      processedDataPoints = processedDataPoints.slice(-22)
    }

    return processedDataPoints
  }, [mapTotalenergySeries, isDailyFormat])

  // Compute a percentile-based max to keep smaller values visible without changing units
  const yAxisMax = useMemo(() => {
    if (!processedData.length) return undefined
    const ys = processedData.map(p => p.y).filter(v => typeof v === 'number' && !isNaN(v) && v >= 0)
    if (!ys.length) return undefined
    const sorted = [...ys].sort((a,b)=>a-b)
    const pIndex = Math.floor(0.95 * (sorted.length - 1))
    const p95 = sorted[pIndex]
    // Add headroom, cap by absolute max to avoid too small max
    const max = Math.max(p95 * 1.2, sorted[Math.min(sorted.length - 1, pIndex)])
    // Ensure max at least top 3rd quartile to avoid over-clipping
    const q3Index = Math.floor(0.75 * (sorted.length - 1))
    const q3 = sorted[q3Index]
    const absMax = sorted[sorted.length - 1]
    const showLabels = processedData.length <= 15
    // For daily views (like 1m), avoid scaling to extreme outliers so smaller bars remain visible
    let finalMax = Math.max(max, q3 * 1.4)
    if (!isDailyFormat && showLabels) {
      // When labels are shown (usually fewer points), ensure a little headroom for label placement
      finalMax = Math.max(finalMax, absMax * 1.05)
    }
    return Number.isFinite(finalMax) ? finalMax : undefined
  }, [processedData, isDailyFormat])

  const updatedChartOptions = {
    ...chartData.options,
    series: [
      {
        name: 'Total Energy',
        data: processedData.map((item) => ({
          x: item.x, // pre-formatted label already
          y: item.y,
          originalDate: item.originalDate,
          originalValue: item.originalValue,
        })),
      },
    ],
    yaxis: {
      min: 0,
      max: yAxisMax,
      labels: {
        formatter: (value) => {
          // Show raw values, no K/M. Use more decimals for small values.
          const abs = Math.abs(value)
          if (abs < 10) return `${value.toFixed(2)} kWh`
          if (abs < 100) return `${value.toFixed(1)} kWh`
          return value % 1 === 0 ? `${Math.floor(value)} kWh` : `${value.toFixed(0)} kWh`
        },
      },
      title: {
        text: 'Total Energy (kWh)',
        style: { color: '#5968B7' },
      },
    },
    tooltip: {
      fixed: {
        enabled: true,
        position: 'topRight',
        offsetX: 0,
        offsetY: 0,
      },
      x: {
        formatter: (value, { seriesIndex, dataPointIndex, w }) => {
          const originalDate = w.globals.initialSeries[seriesIndex].data[dataPointIndex].originalDate
          return isDailyFormat
            ? dayjs(originalDate).format('DD MMMM YYYY')
            : dayjs(originalDate).format('MMMM YYYY')
        },
      },
      y: {
        formatter: (value) => {
          const abs = Math.abs(value)
          if (abs < 10) return `${value.toFixed(2)} kWh`
          if (abs < 100) return `${value.toFixed(1)} kWh`
          return value % 1 === 0 ? `${Math.floor(value)} kWh` : `${value.toFixed(1)} kWh`
        },
      },
    },
    xaxis: {
      type: 'category',
      labels: {
        formatter: (value) => value,
        rotate: -30, // Set rotation angle for better visibility
        style: {
          fontSize: '12px',
          cssClass: 'xaxis-label',
        },
        offsetY: 5,
      },
      tickPlacement: 'on',
    },
    plotOptions: {
      bar: {
        columnWidth: '75%',
        dataLabels: {
          position: 'top', // top | center | bottom
        },
      },
    },
    colors: ['#5968B7'],
    fill: {
      colors: ['#5968B7'],
      type: 'solid',
    },
    dataLabels: {
      enabled: processedData.length <= 15,
      formatter: (val) => {
        const abs = Math.abs(val)
        if (abs < 10) return `${val.toFixed(2)} kWh`
        if (abs < 100) return `${val.toFixed(1)} kWh`
        return val % 1 === 0 ? `${Math.floor(val)} kWh` : `${val.toFixed(0)} kWh`
      },
      offsetY: -15,
      style: { fontSize: '11px', colors: ['#304758'] },
    },
  }

  return (
    <div className={classes.root}>
      {openTable ? (
        <div style={{ maxHeight: '360px', overflow: 'auto' }}>
          <TableContainer component={Paper}>
            <Table aria-label="energy data table">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell
                    align="right"
                    style={{ textTransform: 'capitalize' }}
                  >
                    Total Energy
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {mapTotalenergySeries[0]?.data?.map((row, index) => (
                  <TableRow
                    key={row.x}
                    className={index % 2 === 0 ? 'whiteRow' : 'grayRow'}
                  >
                    <TableCell>{dayjs(row.x).format('YYYY/MM/DD')}</TableCell>
                    <TableCell align="right">
                      {Number(row.y) % 1 === 0 ? Math.floor(Number(row.y)) : Number(row.y).toFixed(1)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      ) : !isData ? (
        <div
          style={{
            padding: '100px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '200px',
            marginTop: '100px',
          }}
        >
          <LinearBuffer />
        </div>
      ) : (
        <div>
          <div
            style={{ color: '#5968B7', fontSize: '12px', marginLeft: '5px' }}
          >
            kWh
          </div>
          <div id="chart">
            <ReactApexChart
              options={updatedChartOptions}
              series={updatedChartOptions.series}
              type="bar"
              height={380}
              ref={chartRef}
            />
          </div>
        </div>
      )}
    </div>
  )
}

EnergyGraphContainer.propTypes = {}

export default EnergyGraphContainer
